import * as PropTypes from "prop-types";
import React from "react";

import { CommentsForm } from "./CommentsForm";
import { CommentsList } from "./CommentsList";

export const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 16000;

const initializeWebSocket = (portfolioId, checklistId) => {
  const socketProtocol =
    window.location.protocol === "https:" ? "wss://" : "ws://";
  const socketUrl = `${socketProtocol}${window.location.host}/ws/comments/${portfolioId}/${checklistId}/`;

  return new WebSocket(socketUrl);
};

class Comments extends React.Component {
  static propTypes = {
    portfolioId: PropTypes.string.isRequired,
    setNrOfComments: PropTypes.func.isRequired,
    selectedItems: PropTypes.array,
    checklistId: PropTypes.string,
  };

  static defaultProps = {
    selectedItems: [],
    checklistId: null,
  };

  state = {
    sentMultiple: false,
    comments: {},
  };

  reconnectAttempts = 0;

  reconnectTimeoutId = null;

  isUnmounted = false;

  componentDidMount() {
    this.isUnmounted = false;
    this.handleWebSockets();
  }

  // eslint-disable-next-line no-unused-vars
  componentWillReceiveProps(nextProps, nextContext) {
    // we should reset sentMultiple if selectedItems were changed to hide latest comment
    if (
      this.state.sentMultiple &&
      this.props.selectedItems.length !== nextProps.selectedItems.length
    ) {
      this.setState({ sentMultiple: false });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.commentsSocket) {
      const socket = this.commentsSocket;
      this.commentsSocket = undefined;
      socket.close(1000);
    }
  }

  submitComment = (commentMessage) => {
    this.commentsSocket.send(
      JSON.stringify({
        comment: commentMessage,
        checklistitems: this.props.selectedItems.map((x) => x.id),
      })
    );
    this.setState({ sentMultiple: this.props.selectedItems.length > 1 });
  };

  handleChecklistCommentsCount = (comments) => {
    // add comments icon if the first comment was added to the empty checklist comments
    Object.values(comments)
      .filter((comment) => comment.length === 1)
      .forEach((comment) => {
        // noinspection JSUnresolvedVariable
        this.props.setNrOfComments(comment[0].checklistitem_id, 1);
      });
  };

  scheduleReconnect = (code) => {
    if (this.isUnmounted || this.reconnectTimeoutId) {
      return;
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error("Comments socket reconnect limit reached", code);
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempts,
      RECONNECT_MAX_DELAY_MS
    );

    this.reconnectAttempts += 1;
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      this.handleWebSockets();
    }, delay);
  };

  handleWebSockets() {
    if (!this.commentsSocket && !this.isUnmounted) {
      const socket = initializeWebSocket(
        this.props.portfolioId,
        this.props.checklistId
      );
      this.commentsSocket = socket;

      socket.onopen = () => {
        if (this.commentsSocket !== socket) {
          return;
        }

        this.reconnectAttempts = 0;
      };

      socket.onmessage = (e) => {
        if (this.commentsSocket !== socket) {
          return;
        }
        const jsonData = JSON.parse(e.data);
        const { comments } = jsonData;

        this.handleChecklistCommentsCount(comments);

        this.setState((prevState) => ({
          // creating a new object for detecting changes in comments list (otherwise useEffect doesn't fire)
          comments: { ...prevState.comments, ...comments },
        }));
      };

      socket.onclose = ({ code }) => {
        if (this.commentsSocket !== socket) {
          return;
        }

        this.commentsSocket = undefined;

        if (code !== 1000) {
          console.error("Comments socket closed unexpectedly", code);
          this.scheduleReconnect(code);
        }
      };
    }
  }

  render = () => (
    <>
      <CommentsList
        selectedItems={this.props.selectedItems}
        comments={this.state.comments}
        sentMultiple={this.state.sentMultiple}
      />
      <CommentsForm
        cellsSelected={this.props.selectedItems.length}
        submitComment={this.submitComment}
      />
    </>
  );
}

export default Comments;
